import { Element } from "@nkitaa/builder-engine";
import { motion } from "framer-motion";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import type { BorderSide, RuntimePropsWithChildren } from "./shared";
import {
  CARD_HOVER_PROPS,
  SECTION_REVEAL_PROPS,
  ThemeSectionContainer,
  classNames,
  extractUtilityClasses,
  getBorderClasses,
  getGridClasses,
  isVisible,
  parseContentAlignmentClass,
  parseHeroOppositeWidth,
  parseHeroWidth,
  toCssLength,
} from "./shared";
import { TextChild } from "./textChild";
import { Button } from "./button";
import { IconChild } from "./iconChild";

export function BlogChild({
  isVisible = true,
  iconAlignment = "left",
  isVisibleIcon = true,
  isVisibleTopic,
  textTopic,
  fontSizeTopic = "text-base",
  fontFamilyTopic,
  textAlignTopic = "left",
  fontColorTopic,
  isVisibleContent,
  textContent,
  fontSizeContent = "text-sm",
  fontFamilyContent,
  textAlignContent = "left",
  fontColorContent,
  isVisibleButton,
  text,
  fontType,
  size = "small",
  fontColor,
  buttonBackgroundColor,
  buttonBorderColor,
  buttonBorder = true,
  rounded,
  underline = false,
  padding = "p-3 wb-md:p-4 wb-lg:p-5",
  distElements = "gap-2",
  backgroundColor = "transparent",
  borderRadius,
  border = true,
  borderColor,
  borderWidth = "border",
  borderStyle = "border-solid",
  borderSides = [],
}: {
  isVisible?: boolean;
  iconAlignment?: "left" | "center" | "right";
  isVisibleIcon?: boolean;
  isVisibleTopic?: boolean;
  textTopic?: string;
  fontSizeTopic?: string;
  fontFamilyTopic?: string;
  textAlignTopic?: "left" | "center" | "right" | "justify";
  fontColorTopic?: string;
  isVisibleContent?: boolean;
  textContent?: string;
  fontSizeContent?: string;
  fontFamilyContent?: string;
  textAlignContent?: "left" | "center" | "right" | "justify";
  fontColorContent?: string;
  isVisibleButton?: boolean;
  text?: string;
  fontType?: string;
  size?: "xlarge" | "large" | "medium" | "small" | "xsmall";
  fontColor?: string;
  buttonBackgroundColor?: string;
  buttonBorderColor?: string;
  buttonBorder?: boolean;
  rounded?: string;
  underline?: boolean;
  padding?: string;
  distElements?: string;
  backgroundColor?: string;
  borderRadius?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: BorderSide[];
}) {
  const { currentColorPalette, currentRounding } = useWBTheme();
  if (!isVisible) return null;
  const roundedClasses = extractUtilityClasses(
    borderRadius ?? currentRounding,
    "rounded",
  );
  const borderClasses = getBorderClasses(
    border,
    borderWidth,
    borderStyle,
    borderSides,
  );
  return (
    <motion.div
      {...CARD_HOVER_PROPS}
      className={classNames(
        "flex flex-col h-full",
        padding,
        borderClasses,
        roundedClasses,
        distElements,
        iconAlignment === "left"
          ? "items-start"
          : iconAlignment === "right"
            ? "items-end"
            : "items-center",
      )}
      style={{
        transition:
          "background-color 300ms ease-in-out, border-color 300ms ease-in-out",
        backgroundColor:
          backgroundColor !== "transparent" ? backgroundColor : undefined,
        borderColor: borderColor ?? currentColorPalette.secondary,
        borderRadius: roundedClasses
          ? undefined
          : toCssLength(borderRadius ?? currentRounding),
      }}
    >
      {isVisibleIcon ? (
        <Element
          is={IconChild}
          id="icon"
          iconName="IconQuote"
          iconSize="medium"
        />
      ) : null}
      <TextChild
        isVisible={isVisibleTopic}
        text={textTopic}
        fontSize={fontSizeTopic}
        fontFamily={fontFamilyTopic}
        textAlign={textAlignTopic}
        color={fontColorTopic}
        headingLevel="h2"
      />
      <TextChild
        isVisible={isVisibleContent}
        text={textContent}
        fontSize={fontSizeContent}
        fontFamily={fontFamilyContent}
        textAlign={textAlignContent}
        color={fontColorContent}
      />
      <Button
        text={text}
        size={size}
        border={buttonBorder}
        backgroundColor={buttonBackgroundColor}
        textColor={fontColor}
        borderColor={buttonBorderColor}
        underline={underline}
        rounded={rounded}
        fontType={fontType}
        isVisible={isVisibleButton}
      />
    </motion.div>
  );
}

export function Blog(props: RuntimePropsWithChildren) {
  if (!isVisible(props.isVisible as boolean | undefined)) return null;
  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const distHeadlineHeader =
    (props.distHeadlineHeader as string | undefined) ?? "mb-2";
  const distHeaderText = (props.distHeaderText as string | undefined) ?? "mb-2";
  const distTextEntries =
    (props.distTextEntries as string | undefined) ?? "gap-3";
  const distEntries = (props.distEntries as string | undefined) ?? "gap-3";
  const layoutWidth =
    (props.layoutWidth as "100%" | "75%" | "50%" | "33%" | "25%" | undefined) ??
    "100%";
  const inversed = (props.inversed as boolean | undefined) ?? false;
  const gridColumns =
    (props.gridColumns as "2" | "3" | "4" | "5" | "6" | undefined) ?? "3";
  const alignmentClass = parseContentAlignmentClass(
    props.contentAlignment as "top" | "center" | "bottom" | undefined,
  );
  const leftWidth = parseHeroWidth(layoutWidth);
  const rightWidth = parseHeroOppositeWidth(layoutWidth);
  return (
    <ThemeSectionContainer
      sectionHeight={props.sectionHeight as number | undefined}
      contentAlignment={
        props.contentAlignment as "top" | "center" | "bottom" | undefined
      }
      backgroundColor={props.backgroundColor as string | undefined}
      padding={padding}
      sectionMargin={sectionMargin}
      border={props.border as boolean | undefined}
      borderColor={props.borderColor as string | undefined}
      borderWidth={props.borderWidth as string | undefined}
      borderStyle={props.borderStyle as string | undefined}
      borderSides={props.borderSides as BorderSide[] | undefined}
      borderRadius={props.borderRadius as string | undefined}
    >
      <motion.div
        {...SECTION_REVEAL_PROPS}
        className={classNames(
          "items-center max-w-275 flex flex-col w-full",
          alignmentClass,
          (props.isVisibleHeadline as boolean | undefined) ||
            (props.isVisibleHeader as boolean | undefined) ||
            (props.isVisibleText as boolean | undefined)
            ? "gap-5"
            : undefined,
        )}
      >
        <div
          className={classNames(
            "flex w-full",
            distTextEntries,
            layoutWidth !== "100%"
              ? inversed
                ? "flex-col wb-md:flex-row-reverse gap-4"
                : "flex-col wb-md:flex-row gap-4"
              : inversed
                ? "flex-col-reverse"
                : "flex-col",
          )}
        >
          <div
            className={classNames(
              "flex flex-col grow justify-center",
              leftWidth,
            )}
          >
            <div className={distHeadlineHeader}>
              <Element
                is={TextChild}
                id="headline"
                isVisible={props.isVisibleHeadline as boolean | undefined}
                text={props.textHeadline as string | undefined}
                fontSize={props.fontSizeHeadline as string | undefined}
                textAlign={
                  props.textAlignHeadline as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h2"
              />
            </div>
            <div className={distHeaderText}>
              <Element
                is={TextChild}
                id="header"
                isVisible={props.isVisibleHeader as boolean | undefined}
                text={props.textHeader as string | undefined}
                fontSize={props.fontSizeHeader as string | undefined}
                textAlign={
                  props.textAlignHeader as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h1"
              />
            </div>
            <Element
              is={TextChild}
              id="text"
              isVisible={props.isVisibleText as boolean | undefined}
              text={props.textText as string | undefined}
              fontSize={props.fontSizeText as string | undefined}
              textAlign={
                props.textAlignText as
                  | "left"
                  | "center"
                  | "right"
                  | "justify"
                  | undefined
              }
            />
          </div>
          <div
            className={classNames(
              getGridClasses(gridColumns, layoutWidth),
              distEntries,
              rightWidth,
            )}
          >
            {props.children}
          </div>
        </div>
      </motion.div>
    </ThemeSectionContainer>
  );
}
